import logger from '../../utils/logger';
import store from '../../redux/store';
import URLPattern from 'url-pattern';
import { auth } from '../../utils/auth';
import { AuthenticatedRoute, AuthState } from '../../types/auth';
import { authenticatedRoutes } from '../../constants/authenticated-routes';
import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { isAuthenticated } from '../../utils/authUtils';
import { messageContents } from '../../constants/message';
import { openMessageModal } from '../../utils/message';

export const authInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const authState = store.getState().auth as AuthState;
      const isUserAuthenticated = isAuthenticated(authState.loginStatus);

      if (!config.url) {
        return config;
      }

      /*  
        Check if the current HTTP request URL belongs to the list of authenticated routes.  
      */
      const matchedRoute = authenticatedRoutes.find((authRoute: AuthenticatedRoute) => {
        const url = authRoute instanceof URLPattern ? authRoute : authRoute.url;
        return url.match(config.url?.split('?')[0] || '');
      });

      /*  
        If the URL is not from authenticated route list, return and let the request proceed.  
      */
      if (!matchedRoute) {
        return config;
      }

      const cancelAxiosCall = (reason?: string) => {
        const controller = new AbortController();
        config.signal = controller.signal;
        controller.abort(`Request cancelled: Bearer token missing in Authorization header.`);
        if (reason) {
          logger('log', reason);
        }
      };

      /*  
        By default, the auth token is included once the application’s auth state is set.  
        However, certain APIs—such as the Companies API—are called immediately after Auth0 authentication  
        but before the application auth state is established, requiring the token to be included.  

        In these cases, we check if `isRequiredForSettingAuthState` is `true`  
        to determine whether the token should be included.  
      */
      const isRequiredToSetAuthState =
        !(matchedRoute instanceof URLPattern) &&
        matchedRoute.isRequiredForSettingAuthState &&
        auth.isAutheticated;

      const isTokenOptional =
        !(matchedRoute instanceof URLPattern) && matchedRoute.authTokenOptional;

      /*  
        Prevent API calls to token-protected endpoints if the user is not logged in 
        or the api call is not part of setting application auth state.  
        This handles cases where the application mistakenly attempts to call a protected API.  
      */
      if (!isTokenOptional && !(isUserAuthenticated || isRequiredToSetAuthState)) {
        cancelAxiosCall(
          'API call to a token-protected endpoint is blocked as the user is not logged in.',
        );
      }

      /*  
        Add the token to the request header if the URL is from authenticated route list.  
      */
      if (isUserAuthenticated || isRequiredToSetAuthState) {
        try {
          const token = await auth.getToken();
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (e) {
          logger('error', 'while getting token at auth-interceptor');
        }
      }

      /*  
        If the user is authenticated or if the api call is part of setting application auth state.
        and the token is missing, handle the following scenarios:  

        1. **For endpoints where the token is optional:**  
            Notify the user and enforce re-login to prevent cases where APIs (e.g., the pricing API)  
            return incorrect data that the user expects based on their logged-in context.  

        2. **For endpoints where the token is required:**  
            Notify the user and enforce re-login to ensure access is restricted as expected.  
      */
      if (
        (isUserAuthenticated || (isRequiredToSetAuthState && !isTokenOptional)) &&
        !config.headers?.Authorization
      ) {
        cancelAxiosCall();
        openMessageModal(messageContents.sessionExpiredErrorMessage);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};
